import React, { useEffect, useState, createContext, useContext } from 'react';
import { supabase } from '../lib/supabase';
// Types
export type ShiftType = 'night' | 'weekend' | 'holiday';
export type Shift = {
  id: number;
  date: string;
  type: ShiftType;
  assignedTo: string[];
  isAvailableForTrade: boolean;
};
export interface Request {
  id: number;
  userId: string;
  shiftId: number;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  approvedBy?: string;
  approvedAt?: string;
}
export type Trade = {
  id: number;
  shiftId: number;
  offeredBy: string;
  takenBy: string | null;
  status: 'available' | 'completed';
};
export type Holiday = {
  id: number;
  name: string;
  date: string;
};
export interface ActivityLog {
  id: number;
  timestamp: string;
  userId?: string;
  userName?: string;
  action: string;
  details: string;
  category: 'login' | 'request' | 'trade' | 'user' | 'holiday' | 'shift' | 'data';
}
type ShiftContextType = {
  shifts: Shift[];
  requests: Request[];
  trades: Trade[];
  holidays: Holiday[];
  activityLogs: ActivityLog[];
  loading: boolean;
  addShift: (shift: Omit<Shift, 'id'>) => Promise<void>;
  updateShift: (id: number, shift: Partial<Shift>) => Promise<void>;
  deleteShift: (id: number) => Promise<void>;
  addRequest: (request: Omit<Request, 'id' | 'status' | 'createdAt'>) => Promise<void>;
  updateRequest: (id: number, status: 'approved' | 'rejected', approvedBy?: string) => Promise<void>;
  addTrade: (shiftId: number, offeredBy: string) => Promise<void>;
  acceptTrade: (tradeId: number, userId: string) => Promise<void>;
  addHoliday: (holiday: Omit<Holiday, 'id'>) => Promise<void>;
  updateHoliday: (id: number, holiday: Partial<Holiday>) => Promise<void>;
  deleteHoliday: (id: number) => Promise<void>;
  assignUserToShift: (shiftId: number, userId: string) => Promise<void>;
  removeUserFromShift: (shiftId: number, userId: string) => Promise<void>;
  addActivityLog: (log: Omit<ActivityLog, 'id' | 'timestamp'>) => Promise<void>;
};
const ShiftContext = createContext<ShiftContextType | undefined>(undefined);
export const ShiftProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [requests, setRequests] = useState<Request[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  // Load shifts with assignments
  const loadShifts = async () => {
    try {
      const {
        data: shiftsData,
        error: shiftsError
      } = await supabase.from('shifts').select('*').order('date');
      if (shiftsError) throw shiftsError;
      const {
        data: assignmentsData,
        error: assignmentsError
      } = await supabase.from('shift_assignments').select('*');
      if (assignmentsError) throw assignmentsError;
      const shiftsWithAssignments = shiftsData.map(shift => ({
        id: shift.id,
        date: shift.date,
        type: shift.type as ShiftType,
        isAvailableForTrade: shift.is_available_for_trade,
        assignedTo: assignmentsData.filter(a => a.shift_id === shift.id).map(a => a.user_id)
      }));
      setShifts(shiftsWithAssignments);
    } catch (error) {
      console.error('Error loading shifts:', error);
    }
  };
  // Load requests
  const loadRequests = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('requests').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;
      const formattedRequests = data.map(r => ({
        id: r.id,
        userId: r.user_id,
        shiftId: r.shift_id,
        status: r.status as 'pending' | 'approved' | 'rejected',
        createdAt: r.created_at,
        approvedBy: r.approved_by || undefined,
        approvedAt: r.approved_at || undefined
      }));
      setRequests(formattedRequests);
    } catch (error) {
      console.error('Error loading requests:', error);
    }
  };
  // Load trades
  const loadTrades = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('trades').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;
      const formattedTrades = data.map(t => ({
        id: t.id,
        shiftId: t.shift_id,
        offeredBy: t.offered_by,
        takenBy: t.taken_by,
        status: t.status as 'available' | 'completed'
      }));
      setTrades(formattedTrades);
    } catch (error) {
      console.error('Error loading trades:', error);
    }
  };
  // Load holidays
  const loadHolidays = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('holidays').select('*').order('date');
      if (error) throw error;
      setHolidays(data);
    } catch (error) {
      console.error('Error loading holidays:', error);
    }
  };
  // Load activity logs
  const loadActivityLogs = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('activity_logs').select('*').order('timestamp', {
        ascending: false
      }).limit(100);
      if (error) throw error;
      const formattedLogs = data.map(log => ({
        id: log.id,
        timestamp: log.timestamp,
        userId: log.user_id || undefined,
        userName: log.user_name || undefined,
        action: log.action,
        details: log.details || '',
        category: log.category as ActivityLog['category']
      }));
      setActivityLogs(formattedLogs);
    } catch (error) {
      console.error('Error loading activity logs:', error);
    }
  };
  useEffect(() => {
    const loadAllData = async () => {
      await Promise.all([loadShifts(), loadRequests(), loadTrades(), loadHolidays(), loadActivityLogs()]);
      setLoading(false);
    };
    loadAllData();
  }, []);
  const addShift = async (shift: Omit<Shift, 'id'>) => {
    try {
      const {
        data,
        error
      } = await supabase.from('shifts').insert({
        date: shift.date,
        type: shift.type,
        is_available_for_trade: shift.isAvailableForTrade
      }).select().single();
      if (error) throw error;
      // Add assignments
      if (shift.assignedTo.length > 0) {
        const assignments = shift.assignedTo.map(userId => ({
          shift_id: data.id,
          user_id: userId
        }));
        await supabase.from('shift_assignments').insert(assignments);
      }
      await loadShifts();
    } catch (error) {
      console.error('Error adding shift:', error);
      throw error;
    }
  };
  const updateShift = async (id: number, shift: Partial<Shift>) => {
    try {
      const updates: any = {};
      if (shift.date !== undefined) updates.date = shift.date;
      if (shift.type !== undefined) updates.type = shift.type;
      if (shift.isAvailableForTrade !== undefined) updates.is_available_for_trade = shift.isAvailableForTrade;
      if (Object.keys(updates).length > 0) {
        const {
          error
        } = await supabase.from('shifts').update(updates).eq('id', id);
        if (error) throw error;
      }
      // Update assignments if provided
      if (shift.assignedTo !== undefined) {
        // Delete existing assignments
        await supabase.from('shift_assignments').delete().eq('shift_id', id);
        // Add new assignments
        if (shift.assignedTo.length > 0) {
          const assignments = shift.assignedTo.map(userId => ({
            shift_id: id,
            user_id: userId
          }));
          await supabase.from('shift_assignments').insert(assignments);
        }
      }
      await loadShifts();
    } catch (error) {
      console.error('Error updating shift:', error);
      throw error;
    }
  };
  const deleteShift = async (id: number) => {
    try {
      const {
        error
      } = await supabase.from('shifts').delete().eq('id', id);
      if (error) throw error;
      await loadShifts();
    } catch (error) {
      console.error('Error deleting shift:', error);
      throw error;
    }
  };
  const addRequest = async (request: Omit<Request, 'id' | 'status' | 'createdAt'>) => {
    try {
      const {
        error
      } = await supabase.from('requests').insert({
        user_id: request.userId,
        shift_id: request.shiftId,
        status: 'pending'
      });
      if (error) throw error;
      await loadRequests();
    } catch (error) {
      console.error('Error adding request:', error);
      throw error;
    }
  };
  const updateRequest = async (id: number, status: 'approved' | 'rejected', approvedBy?: string) => {
    try {
      const {
        error
      } = await supabase.from('requests').update({
        status,
        approved_by: approvedBy,
        approved_at: new Date().toISOString()
      }).eq('id', id);
      if (error) throw error;
      if (status === 'approved') {
        const request = requests.find(r => r.id === id);
        if (request) {
          await assignUserToShift(request.shiftId, request.userId);
        }
      }
      await loadRequests();
    } catch (error) {
      console.error('Error updating request:', error);
      throw error;
    }
  };
  const addTrade = async (shiftId: number, offeredBy: string) => {
    try {
      const {
        error: tradeError
      } = await supabase.from('trades').insert({
        shift_id: shiftId,
        offered_by: offeredBy,
        status: 'available'
      });
      if (tradeError) throw tradeError;
      const {
        error: shiftError
      } = await supabase.from('shifts').update({
        is_available_for_trade: true
      }).eq('id', shiftId);
      if (shiftError) throw shiftError;
      await loadTrades();
      await loadShifts();
    } catch (error) {
      console.error('Error adding trade:', error);
      throw error;
    }
  };
  const acceptTrade = async (tradeId: number, userId: string) => {
    try {
      const trade = trades.find(t => t.id === tradeId);
      if (!trade) return;
      // Update trade
      const {
        error: tradeError
      } = await supabase.from('trades').update({
        taken_by: userId,
        status: 'completed'
      }).eq('id', tradeId);
      if (tradeError) throw tradeError;
      // Remove old user and add new user
      await removeUserFromShift(trade.shiftId, trade.offeredBy);
      await assignUserToShift(trade.shiftId, userId);
      // Mark shift as no longer available for trade
      const {
        error: shiftError
      } = await supabase.from('shifts').update({
        is_available_for_trade: false
      }).eq('id', trade.shiftId);
      if (shiftError) throw shiftError;
      await loadTrades();
      await loadShifts();
    } catch (error) {
      console.error('Error accepting trade:', error);
      throw error;
    }
  };
  const addHoliday = async (holiday: Omit<Holiday, 'id'>) => {
    try {
      const {
        data,
        error
      } = await supabase.from('holidays').insert({
        name: holiday.name,
        date: holiday.date
      }).select().single();
      if (error) throw error;
      // Create holiday shift
      await addShift({
        date: holiday.date,
        type: 'holiday',
        assignedTo: [],
        isAvailableForTrade: false
      });
      await loadHolidays();
    } catch (error) {
      console.error('Error adding holiday:', error);
      throw error;
    }
  };
  const updateHoliday = async (id: number, holiday: Partial<Holiday>) => {
    try {
      const {
        error
      } = await supabase.from('holidays').update({
        name: holiday.name,
        date: holiday.date
      }).eq('id', id);
      if (error) throw error;
      await loadHolidays();
    } catch (error) {
      console.error('Error updating holiday:', error);
      throw error;
    }
  };
  const deleteHoliday = async (id: number) => {
    try {
      const holiday = holidays.find(h => h.id === id);
      if (!holiday) return;
      // Delete holiday shift
      const holidayShift = shifts.find(s => s.type === 'holiday' && s.date === holiday.date);
      if (holidayShift) {
        await deleteShift(holidayShift.id);
      }
      const {
        error
      } = await supabase.from('holidays').delete().eq('id', id);
      if (error) throw error;
      await loadHolidays();
    } catch (error) {
      console.error('Error deleting holiday:', error);
      throw error;
    }
  };
  const assignUserToShift = async (shiftId: number, userId: string) => {
    try {
      const {
        error
      } = await supabase.from('shift_assignments').insert({
        shift_id: shiftId,
        user_id: userId
      });
      if (error && error.code !== '23505') throw error; // Ignore duplicate key errors
      await loadShifts();
    } catch (error) {
      console.error('Error assigning user to shift:', error);
      throw error;
    }
  };
  const removeUserFromShift = async (shiftId: number, userId: string) => {
    try {
      const {
        error
      } = await supabase.from('shift_assignments').delete().eq('shift_id', shiftId).eq('user_id', userId);
      if (error) throw error;
      await loadShifts();
    } catch (error) {
      console.error('Error removing user from shift:', error);
      throw error;
    }
  };
  const addActivityLog = async (log: Omit<ActivityLog, 'id' | 'timestamp'>) => {
    try {
      const {
        error
      } = await supabase.from('activity_logs').insert({
        user_id: log.userId,
        user_name: log.userName,
        action: log.action,
        details: log.details,
        category: log.category
      });
      if (error) throw error;
      await loadActivityLogs();
    } catch (error) {
      console.error('Error adding activity log:', error);
    }
  };
  return <ShiftContext.Provider value={{
    shifts,
    requests,
    trades,
    holidays,
    activityLogs,
    loading,
    addShift,
    updateShift,
    deleteShift,
    addRequest,
    updateRequest,
    addTrade,
    acceptTrade,
    addHoliday,
    updateHoliday,
    deleteHoliday,
    assignUserToShift,
    removeUserFromShift,
    addActivityLog
  }} data-id="element-466">
      {children}
    </ShiftContext.Provider>;
};
export const useShift = (): ShiftContextType => {
  const context = useContext(ShiftContext);
  if (context === undefined) {
    throw new Error('useShift must be used within a ShiftProvider');
  }
  return context;
};